import React, { useState, useEffect, useMemo } from "react";
import { Globe, X, MapPin, Heart, Sparkles, Mic, Image, MessageSquare, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

export interface GlobalMemory {
  id: string;
  user_id: string;
  memory_type: "pop" | "match" | "conversation" | "resonance" | "echo";
  country_code?: string;
  city_id?: string;
  city_name?: string;
  scene_type?: string;
  mood?: string;
  voice_note_url?: string;
  photo_url?: string;
  shared_phrase?: string;
  cultural_exchange?: string;
  matched_with_id?: string;
  created_at: string;
  source_context?: any;
}

interface GlobalMemoryLogsProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalMemoryLogs: React.FC<GlobalMemoryLogsProps> = ({ isOpen, onClose }) => {
  const { user } = useAuthContext();
  const [memories, setMemories] = useState<GlobalMemory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMemory, setSelectedMemory] = useState<GlobalMemory | null>(null);

  // Fetch memories from database (using matches, pops, and a new global_memories table if needed)
  useEffect(() => {
    if (!isOpen || !user) return;

    const fetchMemories = async () => {
      setLoading(true);
      try {
        // Fetch matches with global_connect context
        const { data: matches } = await supabase
          .from("matches")
          .select("id, user1_id, user2_id, created_at, match_source, source_context")
          .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
          .eq("match_source", "global_connect")
          .order("created_at", { ascending: false })
          .limit(50);

        // Fetch pops with global_connect context
        const { data: pops } = await (supabase
          .from("pops") as any)
          .select("id, popper_id, popped_id, created_at, global_connect_country, source_context")
          .or(`popper_id.eq.${user.id},popped_id.eq.${user.id}`)
          .not("global_connect_country", "is", null)
          .order("created_at", { ascending: false })
          .limit(50);

        // Transform to GlobalMemory format
        const memoryList: GlobalMemory[] = [];

        matches?.forEach((match) => {
          const otherUserId = match.user1_id === user.id ? match.user2_id : match.user1_id;
          const ctx = match.source_context as any;
          memoryList.push({
            id: match.id,
            user_id: user.id,
            memory_type: "match",
            country_code: ctx?.country_code,
            city_id: ctx?.city_id,
            city_name: ctx?.city_name,
            scene_type: ctx?.scene,
            matched_with_id: otherUserId,
            created_at: match.created_at,
            source_context: match.source_context,
          });
        });

        pops?.forEach((pop) => {
          const ctx = pop.source_context as any;
          memoryList.push({
            id: pop.id,
            user_id: user.id,
            memory_type: "pop",
            country_code: pop.global_connect_country || ctx?.country_code,
            city_id: ctx?.city_id,
            city_name: ctx?.city_name,
            scene_type: ctx?.scene,
            created_at: pop.created_at,
            source_context: pop.source_context,
          });
        });

        // Sort by date
        memoryList.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        setMemories(memoryList);
      } catch (error) {
        console.error("Error fetching memories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMemories();
  }, [isOpen, user]);

  // Group memories by country/city
  const groupedMemories = useMemo(() => {
    const groups: Record<string, GlobalMemory[]> = {};
    memories.forEach((mem) => {
      const key = `${mem.country_code || "unknown"}-${mem.city_id || "unknown"}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(mem);
    });
    return groups;
  }, [memories]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-4xl h-[90vh] bg-white rounded-3xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6 text-primary" />
            <div>
              <h2 className="text-xl font-bold">Global Memory Logs</h2>
              <p className="text-sm text-muted-foreground">Your journey across the world</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Globe View - Interactive Map */}
        <div className="flex-1 overflow-hidden flex">
          {/* Map/Globe Visualization */}
          <div className="flex-1 relative bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
            <div className="relative w-full h-full">
              {/* Simplified world map background */}
              <div className="absolute inset-0 opacity-20">
                <svg viewBox="0 0 1000 500" className="w-full h-full">
                  {/* Simplified continents */}
                  <path d="M100,200 Q200,150 300,200 T500,200" stroke="currentColor" fill="none" strokeWidth="2" />
                  <path d="M600,180 Q700,130 800,180" stroke="currentColor" fill="none" strokeWidth="2" />
                </svg>
              </div>

              {/* Memory Pins */}
              {Object.entries(groupedMemories).map(([key, mems]) => {
                const mem = mems[0];
                // Simplified positioning (would use real lat/lng in production)
                const x = 20 + (key.charCodeAt(0) % 80);
                const y = 20 + (key.charCodeAt(key.length - 1) % 60);
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedMemory(mem)}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
                    style={{ left: `${x}%`, top: `${y}%` }}
                  >
                    <div className="relative">
                      <MapPin className="w-8 h-8 text-primary drop-shadow-lg animate-pulse" />
                      <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 border-2 border-white flex items-center justify-center text-xs text-white font-bold">
                        {mems.length}
                      </div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {mem.city_name || mem.country_code} • {mems.length} {mems.length === 1 ? "memory" : "memories"}
                      </div>
                    </div>
                  </button>
                );
              })}

              {Object.keys(groupedMemories).length === 0 && !loading && (
                <div className="absolute inset-0 flex items-center justify-center text-center">
                  <div>
                    <Globe className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">No memories yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Start connecting to build your global journey</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Memory Timeline */}
          <div className="w-96 border-l border-border bg-muted/20">
            <ScrollArea className="h-full">
              <div className="p-4 space-y-3">
                <div className="text-sm font-semibold mb-4">Timeline</div>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading memories...</div>
                ) : memories.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No memories yet</div>
                ) : (
                  memories.map((mem) => (
                    <button
                      key={mem.id}
                      onClick={() => setSelectedMemory(mem)}
                      className={cn(
                        "w-full p-3 rounded-xl border text-left transition-all",
                        selectedMemory?.id === mem.id
                          ? "border-primary bg-primary/5"
                          : "border-border bg-white hover:border-primary/30"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {mem.memory_type === "match" && <Heart className="w-5 h-5 text-rose-500" />}
                          {mem.memory_type === "pop" && <Sparkles className="w-5 h-5 text-orange-500" />}
                          {mem.memory_type === "echo" && <Mic className="w-5 h-5 text-blue-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">
                            {mem.memory_type === "match" && "Match"}
                            {mem.memory_type === "pop" && "Pop"}
                            {mem.memory_type === "echo" && "Echo Memory"}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {mem.city_name || mem.country_code} • {mem.scene_type}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(mem.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Memory Detail Modal */}
        {selectedMemory && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm flex items-center justify-center p-6">
            <div className="w-full max-w-md bg-white rounded-2xl border border-border p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold">
                    {selectedMemory.memory_type === "match" && "Match"}
                    {selectedMemory.memory_type === "pop" && "Pop"}
                    {selectedMemory.memory_type === "echo" && "Echo Memory"}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedMemory.city_name || selectedMemory.country_code} • {selectedMemory.scene_type}
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedMemory(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {selectedMemory.voice_note_url && (
                <div className="mb-3 p-3 rounded-lg bg-muted/40 flex items-center gap-2">
                  <Mic className="w-4 h-4 text-primary" />
                  <span className="text-sm">Voice note available</span>
                </div>
              )}

              {selectedMemory.photo_url && (
                <div className="mb-3 rounded-lg overflow-hidden">
                  <img src={selectedMemory.photo_url} alt="Memory" className="w-full h-48 object-cover" />
                </div>
              )}

              {selectedMemory.shared_phrase && (
                <div className="mb-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="text-xs text-muted-foreground mb-1">Shared Phrase</div>
                  <div className="text-sm font-medium">{selectedMemory.shared_phrase}</div>
                </div>
              )}

              {selectedMemory.mood && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Mood: </span>
                  <span className="font-medium">{selectedMemory.mood}</span>
                </div>
              )}

              <div className="mt-4 text-xs text-muted-foreground">
                {new Date(selectedMemory.created_at).toLocaleString()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalMemoryLogs;

